import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Toast from '../../../components/Toast/Toast'
import { TAGS } from '../../../services/Tags'
import {
  createPromptTemplate,
  getPromptTemplates,
  updatePromptTemplate,
  deletePromptTemplate,
} from '../../../services/customPrompts'

// ─── Constants ────────────────────────────────────────────────────────────────

const accent = '#01A76A'
const accentLight = 'rgba(1,167,106,0.08)'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Tag = ({ children, color = accent, bg = accentLight }) => (
  <span
    style={{
      display: 'inline-block',
      background: bg,
      color,
      border: `1px solid ${color}30`,
      borderRadius: 20,
      padding: '3px 11px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.03em',
    }}
  >
    {children}
  </span>
)

const SkeletonCard = () => (
  <div
    style={{
      borderRadius: 12,
      padding: '16px 18px',
      border: '1.5px solid #eef0f3',
      background: '#fafafa',
    }}
  >
    <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    {[{ w: '35%', h: 14 }, { w: '95%', h: 11 }, { w: '80%', h: 11 }, { w: '60%', h: 11 }].map((s, i) => (
      <div
        key={i}
        style={{
          height: s.h,
          width: s.w,
          borderRadius: 6,
          background: 'linear-gradient(90deg,#e8ecf0 25%,#f5f6f8 50%,#e8ecf0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
          marginBottom: i < 3 ? 8 : 0,
        }}
      />
    ))}
  </div>
)

// ─── Form panel ───────────────────────────────────────────────────────────────

const FormPanel = ({ editingTemplate, onCancel, onCreate, onUpdate, isCreating, isUpdating, panelHeight }) => {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: { name: '', text: '' },
  })

  const isEditing = Boolean(editingTemplate)
  const textValue = watch('text', '')

  useEffect(() => {
    if (editingTemplate) {
      setValue('name', editingTemplate.name ?? '')
      setValue('text', editingTemplate.text ?? '')
    } else {
      reset()
    }
  }, [editingTemplate, setValue, reset])

  const handleCancel = () => { reset(); onCancel() }

  const onSubmit = (data) => {
    const payload = { name: data.name, text: data.text }
    if (isEditing) {
      onUpdate({ id: editingTemplate.id, ...payload })
    } else {
      onCreate(payload)
      reset()
    }
  }

  const inputBase = {
    borderRadius: 8,
    fontSize: 13,
    border: '1.5px solid #e0e4ea',
    padding: '9px 13px',
    width: '100%',
    outline: 'none',
    background: '#fff',
    color: '#222',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: isEditing ? `2px solid ${accent}` : '1.5px solid #e8ecf0',
        boxShadow: isEditing ? `0 0 0 4px ${accentLight}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        height: panelHeight,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        overflow: 'hidden',
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid #f0f2f5',
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: isEditing ? accentLight : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {isEditing ? '✏️' : '➕'}
          </div>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e', lineHeight: 1.3 }}>
              {isEditing ? 'Editar prompt' : 'Nuevo prompt'}
            </h6>
            <span style={{ fontSize: 11, color: isEditing ? accent : '#aaa', fontWeight: 600 }}>
              {isEditing ? 'Modo edición activo' : 'Completa los campos y guarda'}
            </span>
          </div>
        </div>
      </div>

      {/* Form body — scrollable */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '18px 22px' }}
      >
        {/* Tag name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
            Tag name <span style={{ color: '#e53935' }}>*</span>
          </label>
          <input
            style={inputBase}
            placeholder="Ej: prompt-match"
            {...register('name', { required: 'El tag name es obligatorio' })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <small style={{ color: '#e53935', fontSize: 11, marginTop: 4, display: 'block' }}>{message}</small>
            )}
          />
        </div>

        {/* Prompt text — grows to fill remaining space */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginBottom: 16 }}>
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', margin: 0 }}>
              Texto del prompt <span style={{ color: '#e53935' }}>*</span>
            </label>
            <span style={{ fontSize: 10, color: '#aaa' }}>{textValue.length} chars</span>
          </div>
          <textarea
            style={{ ...inputBase, flex: 1, resize: 'none', minHeight: 120 }}
            placeholder="Escribe el contenido del prompt aquí..."
            {...register('text', { required: 'El texto es obligatorio' })}
          />
          <ErrorMessage
            errors={errors}
            name="text"
            render={({ message }) => (
              <small style={{ color: '#e53935', fontSize: 11, marginTop: 4, display: 'block' }}>{message}</small>
            )}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            style={{
              background: isCreating || isUpdating ? '#b2dfdb' : accent,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontSize: 13,
              fontWeight: 700,
              cursor: isCreating || isUpdating ? 'not-allowed' : 'pointer',
              width: '100%',
              transition: 'background 0.2s',
              letterSpacing: '0.01em',
            }}
          >
            {isCreating || isUpdating ? 'Guardando...' : isEditing ? '✓ Actualizar prompt' : '+ Agregar prompt'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: '#f3f4f6',
                color: '#666',
                border: 'none',
                borderRadius: 9,
                padding: '10px 0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Cancelar edición
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ─── Template card ────────────────────────────────────────────────────────────

const PromptViewerModal = ({ tpl, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 860,
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #eaecf0',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0d1117' }}>
              {tpl.name || 'Prompt'}
            </div>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
              {tpl.text?.length ?? 0} caracteres
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f5f6f8',
              border: '1px solid #dde1e7',
              borderRadius: 8,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 16,
              color: '#555',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
          }}
        >
          <pre
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.75,
              color: '#1a1a2e',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
              background: '#f8f9fb',
              border: '1px solid #eaecf0',
              borderRadius: 10,
              padding: '20px 22px',
            }}
          >
            {tpl.text}
          </pre>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #eaecf0',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: accent,
              border: 'none',
              borderRadius: 8,
              padding: '8px 22px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

const TemplateCard = ({ tpl, isEditing, onEdit, onDelete, isDeleting }) => {
  const [expanded, setExpanded] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const isProtected = tpl.type_prompt_template_id === 4 || tpl.id === 14
  const isLong = (tpl.text?.length ?? 0) > 180

  return (
    <>
      {showModal && <PromptViewerModal tpl={tpl} onClose={() => setShowModal(false)} />}
      <div
        style={{
          borderRadius: 12,
          padding: '14px 16px',
          border: isEditing ? `1.5px solid ${accent}` : '1.5px solid #eaecf0',
          background: isEditing ? accentLight : '#fff',
          transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
          boxShadow: isEditing ? `0 0 0 3px ${accentLight}` : '0 1px 4px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {/* Top row: tags + actions */}
        <div className="d-flex justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
            {tpl.name && <Tag>{tpl.name}</Tag>}
            {isProtected && (
              <Tag color="#888" bg="rgba(0,0,0,0.04)">🔒 protegido</Tag>
            )}
          </div>
          <div className="d-flex gap-1 flex-shrink-0">
            <button
              onClick={() => setShowModal(true)}
              title="Ver prompt completo"
              style={{
                background: '#f5f6f8',
                border: '1px solid #dde1e7',
                borderRadius: 7,
                padding: '5px 13px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                color: '#444',
                whiteSpace: 'nowrap',
              }}
            >
              Ver
            </button>
            <button
              onClick={() => onEdit(tpl)}
              style={{
                background: isEditing ? accent : '#f5f6f8',
                border: `1px solid ${isEditing ? accent : '#dde1e7'}`,
                borderRadius: 7,
                padding: '5px 13px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                color: isEditing ? '#fff' : '#444',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {isEditing ? '✓ Editando' : 'Editar'}
            </button>
            {!isProtected && (
              <button
                onClick={() => onDelete(tpl.id)}
                disabled={isDeleting}
                style={{
                  background: '#fff8f8',
                  border: '1px solid #ffcdd2',
                  borderRadius: 7,
                  padding: '5px 13px',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  color: '#e53935',
                  whiteSpace: 'nowrap',
                }}
              >
                Eliminar
              </button>
            )}
          </div>
        </div>

        {/* Prompt text */}
        <div>
          <p
            style={{
              fontSize: 12,
              color: '#556',
              margin: 0,
              lineHeight: 1.65,
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: expanded ? 'unset' : 3,
            }}
          >
            {tpl.text}
          </p>
          {isLong && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                padding: '5px 0 0',
                fontSize: 11,
                fontWeight: 700,
                color: accent,
                cursor: 'pointer',
              }}
            >
              {expanded ? '▲ Ver menos' : '▼ Ver más'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const PANEL_HEIGHT = 'calc(100vh - 220px)'

const pageStyles = `
  .custom-prompts-wrapper.dashboard-wrapper {
    margin-left: 24px !important;
    margin-right: 24px !important;
    min-width: 0;
  }
`

const CustomPromptsPage = () => {
  const queryClient = useQueryClient()
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [search, setSearch] = useState('')

  const { data: templatesData, isLoading } = useQuery(
    [TAGS.prompt_templates],
    () => getPromptTemplates({ page: 0 }),
    { keepPreviousData: true }
  )

  const rawTemplates = Array.isArray(templatesData)
    ? templatesData
    : templatesData?.count ?? templatesData?.data ?? templatesData?.templates ?? []

  const templates = search.trim()
    ? rawTemplates.filter(
        (t) =>
          t.name?.toLowerCase().includes(search.toLowerCase()) ||
          t.text?.toLowerCase().includes(search.toLowerCase())
      )
    : rawTemplates

  const invalidate = () => queryClient.invalidateQueries([TAGS.prompt_templates])

  const { mutate: createTemplate, isLoading: isCreating } = useMutation(createPromptTemplate, {
    onSuccess: () => { invalidate(); Toast({ title: 'Prompt creado correctamente' }) },
  })

  const { mutate: updateTemplate, isLoading: isUpdating } = useMutation(updatePromptTemplate, {
    onSuccess: () => {
      invalidate()
      setEditingTemplate(null)
      Toast({ title: 'Prompt actualizado correctamente' })
    },
  })

  const { mutate: deleteTemplate, isLoading: isDeleting } = useMutation(deletePromptTemplate, {
    onSuccess: () => { invalidate(); Toast({ title: 'Prompt eliminado', icon: 'info' }) },
  })

  const startEdit = useCallback((tpl) => setEditingTemplate(tpl), [])
  const cancelEdit = useCallback(() => setEditingTemplate(null), [])

  return (
    <>
      <style>{pageStyles}</style>
      <DashboardWrapper dashboardWrapperClassName="flex-grow-1 custom-prompts-wrapper">
      {/* Page header */}
      <div
        className="pt-5 mt-5"
        style={{
          background: 'linear-gradient(135deg, #01A76A 0%, #019960 100%)',
          borderRadius: 14,
          padding: '24px 32px',
          marginBottom: 20,
          color: '#fff',
        }}
      >
        <h4 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>Prompts personalizados</h4>
        <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>
          Gestiona los prompts utilizados por el sistema.
        </p>
      </div>

      {/* Split layout — fills full width */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 20,
          alignItems: 'start',
          width: '100%',
        }}
      >
        {/* Left — form */}
        <FormPanel
          editingTemplate={editingTemplate}
          onCancel={cancelEdit}
          onCreate={createTemplate}
          onUpdate={updateTemplate}
          isCreating={isCreating}
          isUpdating={isUpdating}
          panelHeight={PANEL_HEIGHT}
        />

        {/* Right — list */}
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1.5px solid #e8ecf0',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            height: PANEL_HEIGHT,
            overflow: 'hidden',
          }}
        >
          {/* List header */}
          <div
            style={{
              padding: '18px 22px 14px',
              borderBottom: '1px solid #f0f2f5',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                Prompts
              </h6>
              {!isLoading && (
                <span
                  style={{
                    background: accentLight,
                    color: accent,
                    borderRadius: 20,
                    padding: '2px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {templates.length}
                </span>
              )}
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar prompt..."
              style={{
                borderRadius: 8,
                border: '1.5px solid #e0e4ea',
                padding: '7px 13px',
                fontSize: 12,
                outline: 'none',
                width: 220,
                color: '#333',
                background: '#fafbfc',
              }}
            />
          </div>

          {/* Scrollable card area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
            {isLoading ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: 14,
                }}
              >
                {[1, 2, 3, 4].map((k) => <SkeletonCard key={k} />)}
              </div>
            ) : templates.length === 0 ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  color: '#aaa',
                  paddingTop: 40,
                }}
              >
                <span style={{ fontSize: 44 }}>📋</span>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>
                  {search ? 'Sin resultados para esa búsqueda.' : 'No hay prompts creados aún.'}
                </p>
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: accent,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: 14,
                  alignContent: 'start',
                }}
              >
                {templates.map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    isEditing={editingTemplate?.id === tpl.id}
                    onEdit={startEdit}
                    onDelete={deleteTemplate}
                    isDeleting={isDeleting}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
    </>
  )
}

export default CustomPromptsPage
